import React, { useState, useEffect, useContext, useCallback } from "react";
import { Modal, Tooltip } from "antd";
import { ICONS } from "../../../assets/icons";
import { ICONSS } from "../../../assets/icons/index1";
import Header from "../../../components/commonComponents/modals/Header";
import FullArrow from "../../../assets/customSVG/FullArrow";
import Loader from "../../../components/commonComponents/Loader/Index";
import { cn } from "../../../helper/cn";
import { billingPlanPricingApi, brandMessageLimitApi } from "../../../api/Api";
import { 
  creditsSendApi, 
  creditsCancelApi, 
  transactionsApi, 
  transactionsResponseApi, 
  brandsInfoApi,
  brandUserEditApi,
  setupApi
} from "../api/Api";
import { useRazorpay } from "react-razorpay";
import NotificationContext from "../../../providers/NotificationProvider";
import { showErrorToast, showSuccessToast } from "../../../components/commonComponents/gxAntdToast/toastUtils";
import { CheckoutContent } from "./CheckoutModal";
import { useSelector, useDispatch } from "react-redux";
import { razorpay_key } from "../../../api/ApiConfig";
import { cityListApi, countryListApi, stateListApi } from "../../crm/api/Api";
import { category as defaultCategory } from "../../auth/components/onboarding/BrandCreation";
import { inputsArray } from "../constants/home";
import { isValidGST } from "../../../helper/validation";

const CheckIcon = ({ color }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="8" fill={color}/>
    <path d="M11.3334 5.33334L6.00008 10.6667L4.66675 9.33334" stroke="white" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const GrayCross = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="8" fill="#AAB0B8"/>
    <path d="M10.6667 5.33334L5.33342 10.6667M5.33342 5.33334L10.6667 10.6667" stroke="white" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PricingPlanModal = ({
  open,
  setOpen,
  brandId,
  brandsData: propsBrandsData,
  setBrandsData: propsSetBrandsData,
  currentAccount: propsCurrentAccount,
  onSuccess
}) => {
  const { Razorpay } = useRazorpay();
  const dispatch = useDispatch();
  const notification = useContext(NotificationContext);
  
  // Redux state
  const reduxBrand = useSelector((state) => state.app.currentBrand);
  const reduxAccount = useSelector((state) => state.app.currentAccount);
  
  // Effective data (props or redux)
  const [brandsData, setBrandsData] = useState(propsBrandsData || reduxBrand);
  const currentAccount = propsCurrentAccount || reduxAccount;

  // Local states for checkout form
  const [loading, setLoading] = useState(false);
  const [createLoader, setCreateLoader] = useState(false);
  const [selectingPlan, setSelectingPlan] = useState(null);
  const [isPlanSelected, setIsPlanSelected] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState("Monthly");
  const [creditsResponse, setCreditsResponse] = useState(null);
  const [locationError, setLocationError] = useState("");
  const [pricingData, setPricingData] = useState([]);
  const [conversationRates, setConversationRates] = useState({
    starter: { marketing: '0.90', utility: '0.15' },
    growth: { marketing: '0.90', utility: '0.15' },
    magnum: { marketing: '0.90', utility: '0.15' }
  });

  // Edit states extracted from BrandOverview logic
  const [editInfo, setEditInfo] = useState(false);
  const [dynamicInputs, setDynamicInputs] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);

  // Sync internal brandsData if props change or redux changes
  useEffect(() => {
    if (propsBrandsData) setBrandsData(propsBrandsData);
    else if (reduxBrand) setBrandsData(reduxBrand);
  }, [propsBrandsData, reduxBrand]);

  useEffect(() => {
    if (open && brandId) {
      fetchPricing();
      fetchConversationRates();
      // Fetch complete brand info to ensure we have currency symbol
      brandsInfoApi(brandId).then((res) => {
        if (res?.status === 200) {
          const fullBrandData = res?.data?.data?.[0] || res?.data?.data || res?.data;
          setBrandsData(fullBrandData);
        }
      });
    }
  }, [open, brandId]);

  const updateData = ({ datas, value }) => {
    if (!value) return datas;
    const res = datas?.map((data) => {
      const { key, secondaryKey } = data;
      if (secondaryKey) {
        const data_id =
          secondaryKey === "country"
            ? value[key]?.[0]?.["country_id"]
            : secondaryKey === "city"
            ? value[key]?.[0]?.["city_id"]
            : secondaryKey === "province"
            ? value[key]?.[0]?.["province_id"]
            : "";
        return {
          ...data,
          value: value[key]?.[0]?.[secondaryKey],
          locations_id: data_id,
        };
      }
      return { ...data, value: value[key] };
    });
    return res;
  };

  useEffect(() => {
    if (brandsData && Object.keys(brandsData).length > 0) {
      setDynamicInputs(updateData({ datas: inputsArray, value: brandsData }));

      // Fetch state/city if brand has them
      const location = brandsData.locations_with_names?.[0];
      if (location?.country_id) {
        stateListApi(location.country_id).then(res => {
          if (res?.status === 200) {
            const states = res?.data?.data?.map(item => ({
              label: item?.name,
              value: item?.name,
              id: item?.id
            }));
            setStateList(states || []);
          }
        });
      }
      if (location?.province_id) {
        cityListApi(location.province_id).then(res => {
          if (res?.status === 200) {
            const cities = res?.data?.data?.map(item => ({
              label: item?.name,
              value: item?.name,
              id: item?.id
            }));
            setCityList(cities || []);
          }
        });
      }
    }
  }, [brandsData]);

  const handleBrandChange = useCallback(({ id, value, locations_id }) => {
    setDynamicInputs((prev) => {
      const updated = prev.map((item) => {
        if (item.id === id) {
          return { ...item, value, locations_id: locations_id || item.locations_id };
        }
        return item;
      });

      if (id === 7) { // State change
        setCityList([]);
        if (locations_id) {
          cityListApi(locations_id).then(res => {
            if (res?.status === 200) {
              const cities = res?.data?.data?.map(item => ({
                label: item?.name,
                value: item?.name,
                id: item?.id
              }));
              setCityList(cities || []);
            }
          });
        }
        // Clear city value (id 8 is city)
        return updated.map(item => item.id === 8 ? { ...item, value: "", locations_id: null } : item);
      }

      return updated;
    });
  }, []);

  const handleUpdate = async () => {
    const payLoad = {
      brand_name: dynamicInputs?.[0]?.value,
      website_url: dynamicInputs?.[2]?.value,
      category: dynamicInputs?.[1]?.value,
      gst_number: dynamicInputs?.[8]?.value,
      address: {
        id: brandsData?.locations_with_names?.[0]?.location,
        address_line_1: dynamicInputs?.[3]?.value,
        country_id: dynamicInputs?.[4]?.locations_id,
        province_id: dynamicInputs?.[5]?.locations_id,
        city_id: dynamicInputs?.[6]?.locations_id || 1,
        postal_index_code: dynamicInputs?.[7]?.value,
      },
    };

    if (!payLoad.brand_name || !payLoad.address.address_line_1 || !payLoad.address.postal_index_code) {
      showErrorToast(notification, "Please fill required fields");
      return;
    }

    if (brandsData?.brand_currency_code === "INR" && payLoad.gst_number && !isValidGST(payLoad.gst_number)) {
      showErrorToast(notification, "Invalid GST format");
      return;
    }

    setLoading(true);
    const res = await brandUserEditApi(brandId, payLoad);
    setLoading(false);
    if (res?.status === 200) {
      showSuccessToast(notification, "Brand info updated");
      setEditInfo(false);
      // Refresh local brand data
      brandsInfoApi(brandId).then(res => {
        if (res?.status === 200) {
          setBrandsData(res.data);
          if (propsSetBrandsData) propsSetBrandsData(res.data);
        }
      });
    } else {
      showErrorToast(notification, res?.response?.data?.error);
    }
  };

  const buttonEnable = () => {
    return dynamicInputs.every(item => {
      if (item.id === 10 || item.id === 8) return true; // Optional
      return !!item.value;
    });
  };

  const fetchPricing = async () => {
    try {
      setLoading(true);
      const res = await billingPlanPricingApi(brandId);
      if (res?.data) {
        setPricingData(res.data);
      }
    } catch (error) {
      console.error("Error fetching pricing:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversationRates = async () => {
    try {
      const planTypes = ['starter', 'growth', 'magnum'];
      const rates = {};
      for (const plan of planTypes) {
        const res = await brandMessageLimitApi(brandId, plan);
        if (res?.data) {
          rates[plan] = {
            marketing: res.data.marketing_cost || '0.00',
            utility: res.data.utility_cost || '0.00'
          };
        }
      }
      setConversationRates(prev => ({ ...prev, ...rates }));
    } catch (error) {
      console.error('Error fetching conversation rates:', error);
    }
  };

  const getPricingContent = (planCode) => {
    const rates = conversationRates[planCode.toLowerCase()] || conversationRates.starter;
    return (
      <ul style={{ listStyleType: "disc", margin: "0", padding: "0 1rem" }} className="font14px-400 text-[var(--font-600)]">
        <li style={{ marginBottom: "0.5rem" }}>
          {brandsData?.brand_currency_symbol}{rates.marketing}/Marketing Messages*
        </li>
        <li style={{ marginBottom: "0.5rem" }}>
          {brandsData?.brand_currency_symbol}{rates.utility}/Utility & Authentication Messages*
        </li>
      </ul>
    );
  };

  const getPlanPrice = (planCode, type) => {
    if (planCode.toLowerCase() === "magnum") return null;
    const plan = pricingData.find(p => p.code === planCode.toLowerCase());
    if (!plan) return null;
    return type === "Monthly" ? plan.monthly_price : plan.annual_price;
  };

  const pricing = [
    {
      id: 1,
      title: "Starter",
      code: "starter",
      icon: ICONS?.Starter,
      features: { verified: true, tick: false },
      content: getPricingContent('starter'),
      subContent: "For startups and solopreneurs",
      btnText: "Choose a plan",
      brandColor: "#25D366",
      buttonClass: "bg-[#E7FDF3] text-[#0F9960] hover:bg-[#D1FAE5]", 
    },
    {
      id: 2,
      title: "Growth",
      code: "growth",
      icon: ICONS?.Growth,
      features: { verified: true, tick: true },
      content: getPricingContent('growth'),
      subContent: "For small to mid-scale businesses",
      btnText: "Choose a plan",
      brandColor: "#0052CC",
      buttonClass: "bg-[#0052CC] text-white hover:bg-[#0042A6]",
    },
    {
      id: 3,
      title: "Magnum",
      code: "magnum",
      icon: ICONS?.Magnum,
      features: { verified: true, tick: true },
      content: getPricingContent('magnum'),
      subContent: "For Enterprise businesses",
      btnText: "Choose a plan",
      brandColor: "#8B5CF6",
      buttonClass: "bg-[#F3F4F6] text-[#1F2937] hover:bg-[#E5E7EB]",
      custom: true,
    },
  ];

  const handleSelectPlans = (p) => {
    setSelectingPlan(p.code);
    const payLoad = {
      brand_id: brandId,
      transaction_type: "SUBSCRIPTION",
      billing_plan_name: p.code,
      billing_cycle: selectedPrice?.toLowerCase(),
    };

    creditsSendApi(payLoad).then((res) => {
      setSelectingPlan(null);
      if (res?.status === 200) {
        setIsPlanSelected(true);
        setSelectedPlan(p);
        setCreditsResponse(res?.data?.credit_item);
        // Refresh brandsData to get newest plan info for GST calculation etc.
        brandsInfoApi(brandId).then(resBrand => {
          if (resBrand?.status === 200) {
             setBrandsData(resBrand.data);
             if (propsSetBrandsData) propsSetBrandsData(resBrand.data);
          }
        });
      } else {
        showErrorToast(notification, res?.response?.data?.error);
      }
    });
  };

  const handleBackPlan = () => {
    const payLoad = {
      brand_id: brandId,
      transaction_item_id: creditsResponse?.id,
    };
    creditsCancelApi(payLoad).then((res) => {
      if (res?.status === 200) {
        setIsPlanSelected(false);
        setSelectedPlan(null);
        setCreditsResponse(null);
      }
    });
  };

  const handleViewPlans = () => {
    handleBackPlan();
  };

  const handleCancel = () => {
    if (isPlanSelected && creditsResponse) {
      const payLoad = {
        brand_id: brandId,
        transaction_item_id: creditsResponse?.id,
      };
      creditsCancelApi(payLoad).then(() => {
        setIsPlanSelected(false);
        setOpen(false);
      });
    } else {
      setOpen(false);
    }
  };

  const handlePayment = (updatedBillSummary, selectedBillingCycle) => {
    setCreateLoader(true);

    // Use the updated bill summary if available (from billing cycle change), otherwise use original creditsResponse
    const billData = updatedBillSummary || creditsResponse;
    const transactionItemId = billData?.credit_item?.id || billData?.id;

    const payLoad = {
      brand_id: brandId,
      transaction_item_id: transactionItemId,
      type: "SUBSCRIPTIONS",
    };

    transactionsApi(payLoad).then((res) => {
      setCreateLoader(false);
      if (res?.status === 200) {
        const options = {
          key: razorpay_key,
          amount: res?.data?.transaction?.total_cost * 100,
          currency: res?.data?.transaction?.currency,
          order_id: res?.data?.transaction?.razerpay_order_id,
          notes: { transaction_id: transactionItemId },
          handler: (response) => {
            transactionsResponseApi(response).then((res) => {
              if (res?.status === 200) {
                showSuccessToast(notification, "Payment Success");
                setIsPlanSelected(false);
                setOpen(false);
                if (onSuccess) onSuccess();
                // Refresh brand data
                brandsInfoApi(brandId).then(res => {
                  if (res?.status === 200) {
                    setBrandsData(res.data);
                    if (propsSetBrandsData) propsSetBrandsData(res.data);
                    // Also fire a setup API if needed by the app
                    setupApi(brandId);
                  }
                });
              }
            });
          },
        };
        const razorpayInstance = new Razorpay(options);
        razorpayInstance.open();
      } else {
        showErrorToast(notification, res?.response?.data?.error);
      }
    });
  };

  return (
    <Modal
      styles={{ content: { padding: 0 } }}
      open={open}
      centered
      footer={null}
      closable={false}
      mask={true}
      wrapClassName={"bg-[#00000095]"}
      destroyOnClose
      width={"70vw"}
      zIndex={1100}
      style={{
        width: "100%",
        borderRadius: 20,
        boxShadow: "0px 10px 24px 0px rgba(79, 92, 128, 0.15)",
        overflow: "hidden",
      }}
    >
      <Header
        title={isPlanSelected ? "Proceed to Checkout" : "Gainwix Plans and Pricing"}
        prefix={
          isPlanSelected ? (
            <div
              className="rotate-180 size-9 flex items-center justify-center border border-[var(--border-100)] rounded-lg cursor-pointer"
              onClick={handleBackPlan}
            >
              <FullArrow width={14} height={14} />
            </div>
          ) : null
        }
        prefixStyle="mr-4"
        titleSize="largest"
        handleCancel={handleCancel}
      />
      
      <div className="bg-[#FFFFFF] px-[2vw] py-[1.5vw]">
        {isPlanSelected ? (
          <CheckoutContent
            isPlanSelected={true}
            handleBackPlan={handleBackPlan}
            locationError={locationError}
            brandsData={brandsData}
            creditsResponse={creditsResponse}
            dynamicInputs={dynamicInputs}
            editInfo={editInfo}
            setEditInfo={setEditInfo}
            handleUpdate={handleUpdate}
            handleBrandChange={handleBrandChange}
            input={"font14px-500 text-[var(--textBlack)] w-full py-2 bg-transparent outline-none"}
            inputContainer={"w-full h-11 flex items-center rounded-lg mt-5"}
            buttonClass={(editInfoState) => {
              if (editInfoState && buttonEnable()) return "bg-[#0F9960] text-white rounded-lg px-10 py-1.5 mb-20 font14px-600";
              if (editInfoState && !buttonEnable())
                return "border border-[var(--border-input)] bg-[var(--BG-50)] text-[var(--disable-color)] rounded-lg px-10 py-1.5 mb-20 font14px-600 cursor-not-allowed";
              return "bg-[#E7FDF3] text-[#0F9960] rounded-lg px-10 py-1.5 mb-20 font14px-600";
            }}
            category={defaultCategory}
            stateList={stateList}
            cityList={cityList}
            buttonEnable={buttonEnable}
            handlePayment={handlePayment}
            currentAccount={currentAccount}
            selectedPlan={selectedPlan}
            selectedPrice={selectedPrice}
            createLoader={createLoader}
            handleViewPlans={handleViewPlans}
          />
        ) : (
          <>
            <div className="flex flex-col items-center justify-center pb-[1.5vw] relative">
              <div className="w-fit relative flex flex-col items-center text-center">
                <div className="mb-[0.8vw]">
                  {/* <span className="px-[0.8vw] py-[0.3vw] bg-[#E7FDF3] text-[#0F9960] rounded-full text-xs font-medium border border-[#0F9960]/20">
                    20% off on Yearly Plan
                  </span> */}
                </div>

                <div className="flex items-center bg-[#F3F4F6] rounded-full p-[0.2vw]">
                  <button
                    className={`px-[1.5vw] py-[0.4vw] rounded-full text-xs font-medium transition-all duration-300 ${
                      selectedPrice === "Monthly"
                        ? "bg-white text-[#0F9960] shadow-sm"
                        : "text-[#6B7280] hover:text-[#374151]"
                    }`}
                    onClick={() => setSelectedPrice("Monthly")}
                  >
                    Monthly
                  </button>
                  <button
                    className={`px-[1.5vw] py-[0.4vw] rounded-full text-xs font-medium transition-all duration-300 ${
                      selectedPrice === "Yearly"
                        ? "bg-white text-[#0F9960] shadow-sm"
                        : "text-[#6B7280] hover:text-[#374151]"
                    }`}
                    onClick={() => setSelectedPrice("Yearly")}
                  >
                    Yearly
                  </button>
                </div>

                <div className="flex flex-col items-center mb-[0.4vw] mt-[0.8vw]">
                  <h2 className="text-[1.8vw] font-bold text-[#111827] mb-[0.3vw]">
                    Choose the <span className="text-[#25D366]">Perfect Plan</span> for You
                  </h2>
                  <p className="text-[#6B7280] text-[0.9vw]">
                    Find the right option and start making the most of our AI-powered platform today!
                  </p>
                </div>
              </div>
            </div>

            <div className="w-full h-fit flex justify-center gap-5">
              {pricing.map((p) => {
                const price = getPlanPrice(p.code, selectedPrice);
                const isCurrentPlan = p.code === brandsData?.current_plan?.toLowerCase();
                const buttonText = isCurrentPlan ? "Current Plan" : (p.custom ? "Contact Sales" : p.btnText);
                const isDisabled = selectingPlan || createLoader || isCurrentPlan;

                const handleButtonClick = () => {
                  if (isDisabled) return;

                  // If custom plan (Magnum), open calendar link
                  if (p.custom) {
                    window.open('https://calendar.app.google/eMb413uHfzqeUv5fA', '_blank', 'noopener,noreferrer');
                  } else {
                    // Regular plan selection
                    handleSelectPlans(p);
                  }
                };

                return (
                  <div
                    key={p.id}
                    className="min-w-[18vw] bg-white rounded-[1.2vw] p-[0.2vw] shadow-[0px_4px_16px_0px_#2D303615] hover:shadow-[0px_8px_24px_0px_#2D303625] transition-shadow duration-300"
                  >
                    <div className="w-auto h-full bg-[var(--white)] rounded-[1.1vw] px-[1.2vw] py-[1.5vw] flex flex-col items-start text-left">
                      <img src={p.icon} alt={p.title} className="size-[3vw] mb-[0.6vw]" />
                      <h3 className="text-[1.6vw] weight-bold text-[#202421]">{p.title}</h3>
                      <p className="text-[#616874] text-xs mt-0.5 mb-1.5">{p.subContent}</p>

                      <div className="mt-[0.4vw] mb-[1.2vw]">
                        <span className="text-[2vw] weight-bold text-[#202421]">
                          {loading ? "..." : (price ? `${brandsData?.brand_currency_symbol}${parseFloat(price).toLocaleString()}` : "Custom")}
                        </span>
                        {!p.custom && price && (
                          <span className="text-[0.8vw] weight-medium text-[#616874] ml-1">
                            {selectedPrice === "Monthly" ? "/month" : "/year"}
                          </span>
                        )}
                      </div>

                      <div className="w-full flex-column mb-[1.2vw]">
                        <div className="flex items-center gap-2.5 mb-3">
                          {p.features?.verified ? <CheckIcon color={p.brandColor} /> : <GrayCross />}
                          <span className="font14px-400 text-[var(--font-600)]">Business verification</span>
                        </div>
                        <div className="flex items-center gap-2.5">
                          {p.features?.tick ? <CheckIcon color={p.brandColor} /> : <GrayCross />}
                          <span className="font14px-400 text-[var(--font-600)]">Green Tick</span>
                        </div>
                      </div>

                      <div className="w-full h-[1px] bg-[var(--border-input)] mb-[1.2vw]"></div>
                      <div className="w-full flex-column mb-[1.2vw] text-left">{p.content}</div>

                      <button
                        className={cn(
                          "w-full py-[0.7vw] flex items-center justify-center gap-[0.4vw] rounded-[0.5vw] text-[0.9vw] font-semibold transition-colors duration-200 mt-auto",
                          isCurrentPlan ? "cursor-not-allowed opacity-60" : "cursor-pointer",
                          p.buttonClass
                        )}
                        onClick={handleButtonClick}
                        disabled={isDisabled}
                      >
                        {selectingPlan === p.code ? <Loader Width={18} Height={18} /> : <p>{buttonText}</p>}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col items-center justify-center mt-[1.5vw] text-center">
              <a href="https://gainwix.ai/pricing" target="_blank" className="px-[0.6vw] py-[0.3vw] border-[0.06vw] border-[var(--border-50)] text-xs weight-medium rounded-[0.3vw] !text-[var(--font-600)] hover:bg-gray-50">
                Compare Plans
              </a>
              <p className="text-xs weight-medium py-[0.8vw] text-[#6B7280]">
                {selectedPrice === "Yearly" ? "You'll be billed once a year for the entire year's access." : "You'll be billed monthly."} This is a non-refundable plan.
                <br />
                Taxes may apply depending on your location. For more information, please go to{" "}
                <a href="https://gainwix.ai/pricing" target="_blank" className="!text-[#6940F2]">https://gainwix.ai/pricing</a>
                <br />
                If you have any concerns write us to <a href="mailto:support@gainwix.com" className="!text-[#6940F2]">support@gainwix.com</a> or reach us @9952166807
              </p>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default PricingPlanModal;
